import * as React from 'react';

/**
 * React-Select, the lib behind Flame Autocomplete, does not expose access to the input element.
 * Pass the event handler that would've been passed to onKeyDown prop of Flame Autocomplete.
 * Pass the event handler that would've been passed to onInputChange prop of Flame Autocomplete.
 * Pass the event handler that would've been passed to onChange prop of Flame Autocomplete.
 * The return value is a string that can be used as the `inputId` prop of the Flame Autocomplete component.
 */
export const useScanReactSelect = ({
  onKeyDown,
  onInputChange,
  onChange,
  inputId: _inputId,
}: {
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputChange?: (value: string) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputId?: string;
}): string => {
  const inputId = React.useRef<string>(
    _inputId || `scan-react-select-${Math.random().toString(36).substring(7)}`
  );
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (onKeyDown) onKeyDown(event as unknown as React.KeyboardEvent<HTMLInputElement>);
    };
    const handleInput = (event: Event) => {
      if (onInputChange)
        onInputChange((event as unknown as React.ChangeEvent<HTMLInputElement>).target.value ?? '');
    };
    const handleChange = (event: Event) => {
      if (onChange) onChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const inputElement = document.getElementById(inputId.current);

    if (inputElement) {
      // Binding directly on onkeypress to allow ScanHandler to detect that this input field is expecting key events.
      inputElement.onkeypress = (event: KeyboardEvent) => {
        handleKeyDown(event);
      };
      // Binding directly on oninput to simplify unbinding of event listener.
      inputElement.oninput = (event: Event) => {
        handleInput(event);
      };
      // Binding directly on onchange to simplify unbinding of event listener.
      inputElement.onchange = (event: Event) => {
        handleChange(event);
      };
      // Allow ScanHandler to detect field presence on the page
      inputElement.classList.add('inputbarcode');
    }

    return () => {
      if (inputElement) {
        inputElement.onkeypress = null;
        inputElement.oninput = null;
        inputElement.onchange = null;
        inputElement.classList.remove('inputbarcode');
      }
    };
  }, [onChange, onInputChange, onKeyDown]);
  return inputId.current;
};
